import Vue from "vue";
import axios from "axios";
import moment from "moment";
import study_utils from "@/libs/study_utils";
import deepSet from "../../libs/deep_set";
import user from "./user";
moment.locale("fr");
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

// === STATE =====================================================================================
const state = {
	saving: false,
	changed: false,
	required: 0,
	last_data: false,
	offline: false,
	saveInterval: false,
	last_save_failed: false,
	lists: {
		segments: [],
		statuts: [],
		templates: [],
		etude_types: [],
		contribution_types: [],
		years: [],
		users: [],
		departements: [],
		code_evalis_list: [],
		contributeurs: []
	},
	active_study: false,
	publication_in_progress: false,
	publication_failed: false,
	publication_message: "Veuillez contacter le support.",
	publication_message_default: "Veuillez contacter le support.",
	source: axios.CancelToken.source(),
	display_template: false
};

// === GETTERS =====================================================================================
const getters = {
	study: function (state) {
		return state;
	},
	active_study: state => state.active_study,
	active_study_is_published: state => {
		return state.active_study.statut.slug === "publie";
	},
	etude_data: state => {
		return state.active_study.etudeData;
	},
	source: state => {
		return state.source;
	},
	display_template: state => {
		const source = state.active_study.source ? state.active_study.source : "";
		return is_display_template(state.active_study.templated, source);
	}
};

// === functions ===================================================================================
async function create_project ({project_values}) {
	try {
		const project_response = await axios.post(API_BASE_URL + "api/projet", project_values);
		return project_response;
	} catch (error) {
		console.error("ERROR while creating the project, project_values ==", project_values);
	}
}

function is_display_template (templated, source){
	return templated && source !== "[MIGRATION] ELASTIC";
}

// === ACTIONS =====================================================================================
const actions = {
	cancel ({state, commit}) {
		state.source.cancel();
		commit("SET_STUDY_STATE", { source: axios.CancelToken.source() });
	},
	async duplicate_study ({dispatch, state}, data){
		try {
			//set datecreate
			await dispatch("edit_object", {
				obj: data,
				field: "dateCreation",
				value: moment().format(),
			});
			//clean cartographie
			await dispatch("edit_object", {
				obj: data,
				field: "etudeData",
				value: {cartographie: {references_prix: []}},
			});
			//add boolean to specify that QlikData need to be refreshed
			await dispatch("edit_object", {
				obj: data,
				field: "etudeData",
				value: {refreshQlikData: true},
			});
			//add boolean to specify that KyaneosData need to be refreshed
			await dispatch("edit_object", {
				obj: data,
				field: "etudeData",
				value: {refreshKyaneosData: true},
			});
			//add boolean to specify that YanportData need to be refreshed
			await dispatch("edit_object", {
				obj: data,
				field: "etudeData",
				value: {refreshYanportData: true},
			});
			// set status to "en-cours"
			const default_status = _.find(state.lists.statuts, ["slug", "en-cours"]);
			await dispatch("edit_object", {
				obj: data,
				field: "statut",
				value: {id: default_status.id},
			});
			// remove pdf
			await dispatch("edit_object", {
				obj: data,
				field: "pdfLien",
				value: "",
			});
			// remove code evalis
			await dispatch("edit_object", {
				obj: data,
				field: "evalisReference",
				value: "",
			});

			const study_response = await axios.post(API_BASE_URL + "api/etude", data);
			await dispatch("edit_object", {
				obj: data.projet,
				field: "dateCommande",
				value: moment().format(),
			});
			await axios.put(`${API_BASE_URL}api/projet/${data.projet.id}`, data.projet);
			return study_response.data.content;
		} catch (e){
			console.error("error while duplicating the study : " + e);
			return false;
		}
	},
	async create_study ({dispatch}, data) {
		if (!data || !data.project_values || !data.study_values) {
			console.error("invalid data for project/study creation", data);
			return;
		}
		if (!data.project_values.adresse.codeInsee) {
			console.error("adresse.codeInsee required in project_values");
			return;
		}
		const project_values = data.project_values;
		const study_values = data.study_values;
		try {
			// ==== 1 - create the project
			const project = {};
			if (!data.existing_project) {
				const project_response = await create_project({ project_values: project_values });
				// console.log("1 - project created => query the Datawarehouse for the stats data", project_response);
				project.id = project_response.data.content;
			} else {
				project.id = project_values.id;
				// need to update project values
				if (_.isEmpty(project_values.pieceJointe)) {
					project_values.pieceJointe = [];
				}
				await axios.put(`${API_BASE_URL}api/projet/${project_values.id}`, project_values);
				// console.log("project successfully saved", response);
			}
			const insee_code = project_values.adresse.codeInsee;

			// ==== 2 - get the stats data
			const segmentId = study_values.segment.id;
			const etudeTypeId = study_values.etudeType.id;
			const templated = state.lists.templates.filter(template => template.segment.id == segmentId && template.etudeType.id == etudeTypeId)[0].templated;
			if (templated){ // pas possible car l'étude n'est pas encore créée
				try {
					
					study_values.qlikData = await study_utils.new_get_qlik_data({
						codeInsee: insee_code,
						segmentId: study_values.segment.id,
						etudeTypeId: study_values.etudeType.id,
						projectLat: project_values.position.location.lat,
						projectLon: project_values.position.location.lon
					});
					try {
						if (study_values.qlikData.length !== 0){
							const c = study_values.qlikData.component.find((c) => {return c.name == "niveau priorite" || c.id == "1.1";});
							if (c) {
								if (!c.content.commune[0].data_list[0].value.match(/^[0-9]*$/) && c.content.commune[0].data_list[0].value.match(/[0-9]+/).length) {
									c.content.commune[0].data_list[0].value = c.content.commune[0].data_list[0].value.match(/[0-9]+/)[0];
								}
							}
						}
					} catch (error) {
						console.error("ERROR in create_study / get Priority - no priority ", error);
					}

					study_values.etudeData = study_utils.setup_study_data(project_values, study_values);


					if (!study_values.etudeData.projet_urbain) {
						study_values.etudeData.projet_urbain = {
							prix: {
								collectif_sans_parking: "",
								individuel_avec_parking: ""
							},
							illustrations: [{src: "", image_zoom: 1, image_rotation: 0, image_offset: {x: 0, y: 0}}, {src: "", image_zoom: 1, image_rotation: 0, image_offset: {x: 0, y: 0}}],
							commentaire: "Commentaire",
							source_image: "à définir",
							impact_amenagement: "0",
							avancee: "0",
							attractivite: {
								actuelle: "0",
								livraison: "0",
								achevement: "0"
							},
							potentiel_eligible: "Préciser",
							commentaire2: "Commentaire CE/consultants"
						};
					}

				} catch (error) {
					console.error("ERROR while retrieving stats data for CodeInsee == ", insee_code, error);
				}
			} else {
				study_values.etudeData = {};
				study_values.qlikData = {};
			}
			// ==== get kyaneos data for SCPI studies

			if (study_values.segment.code === "SCPI") {
				study_values.etudeData.kyaneosData = await study_utils.get_kyaneos_scpi_data({id: project_values.customGeoDetails.kyaneos.Guid});
			}

			// ==== get yanport data for SCPI studies

			if (study_values.segment.code === "SCPI") {
				study_values.etudeData.yanportData4 = await study_utils.get_yanport_scpi_data({
					codeInsee: project_values.adresse.codeInsee,
					forceVille: study_values.etudeData.scpi.yanport["page_4"].localisations,
					searchedData: ["tableauLocatif", "kpiLocatif"],
					typeBiens: study_values.etudeData.scpi.yanport["page_4"].type_biens,
					periodeAnalyse: study_values.etudeData.scpi.yanport["page_4"].periode_analyse
				});
				study_values.etudeData.yanportData5 = await study_utils.get_yanport_scpi_data({
					codeInsee: project_values.adresse.codeInsee,
					forceVille: study_values.etudeData.scpi.yanport["page_5"].localisations,
					searchedData: ["kpiAncien"],
					typeBiens: study_values.etudeData.scpi.yanport["page_5"].type_biens,
					periodeAnalyse: study_values.etudeData.scpi.yanport["page_5"].periode_analyse
				});
			}

			// ==== 3 - create the study
			study_values.projet.id = project.id;
			// console.log("study creation with data", study_values);
			try {
				const study_response = await axios.post(API_BASE_URL + "api/etude", study_values);
				// console.log("1 - study created => we can display it", study_response);

				dispatch("set_window", { name: false });
				// load created study
				if (data && data.callback) {
					// console.log("study created, calling callback with study id", study_response.data.content);
					data.callback({ id: study_response.data.content });
				}
			} catch (error) {
				console.error("ERROR while creating the study", error);
			}
		} catch (error) {
			console.error("ERROR while creating the project", error);
		}
	},

	async load_study ({ commit, dispatch, state }, data) {
		const study_id = data.id;
		this.dispatch("cancel");
		try {
			const study_response = await axios.get(`${API_BASE_URL}api/etude/${study_id}`);
			const study = study_response.data.content;

			if (Array.isArray(study.etudeData.parameters)){
				study.etudeData.parameters = {};
			}

			let zone_geo = undefined;
			try {
				zone_geo = study.etudeData.promotion.zone_geo;
			} catch (e){
				console.error(e);
			}
			// console.log("loaded study", study);

			// load the latest update of the project
			await dispatch("load_active_project", { project_id: study.projet.id });

			if (is_display_template(study.templated, study.source)){
				if (_.isEmpty(study.etudeData)) {
					study.etudeData = study_utils.setup_study_data(study.projet); // _.cloneDeep(STUDY_DATA_TEMPLATE);
				} else {
					study.etudeData = study_utils.sync_study_data(study.etudeData, study.qlikData);
				}
			}
				
			if (!study.template) {
				// get template from template list
				const templates_by_segment = _.groupBy(state.lists.templates, t => {
					return t.segment.code;
				});
				for (const segment in templates_by_segment) {
					templates_by_segment[segment] = _.keyBy(templates_by_segment[segment], t => {
						return t.etudeType.slug;
					});
				}
				study.template = null;
				if (_.has(templates_by_segment, [study.segment.code, study.etudeType.slug])) {
					study.template = templates_by_segment[study.segment.code][study.etudeType.slug];
				}
			}

			if (zone_geo) study.etudeData.promotion.zone_geo = zone_geo;
			dispatch("set_active_study", {active_study: null});//empty the study before openning a new one
			dispatch("set_active_study", { active_study: study });
			dispatch("set_mode", { mode: "viewing" });

			commit("SET_STUDY_STATE", { changed: false });
			if (data && data.callback) {
				data.callback();
			}
		} catch (error) {
			console.error("ERROR while retrieving study data", study_id, error);
		}
	},

	async update_study_and_project ({ commit, dispatch, state }, {data, refresh_stat_data}) {
		if (!data || !data.project_values || !data.study_values) {
			throw Error("Vuex action update_study_and_project - invalid data");
		}

		dispatch("set_saving", { saving: true });
		const project_values = data.project_values;
		commit("EDIT_OBJECT", {
			obj: state.active_study.projet.adresse,
			field: "InseeCode",
			value: project_values.adresse.codeInsee,
		});
		try {
			const r = await axios.get(`${API_BASE_URL}api/projet/${project_values.id}`);
			const project = r.data.content;
			// console.log("loaded project", project);

			const keys_to_keep = [
				"promoteur",
				"statut",
				"segment",
				"position",
				"adresse",
				"customGeoDetails",
				"contributeurAdequation",
				"nom",
			];
			for (const key of _.intersection(_.keys(project_values), keys_to_keep)) {
				// console.log('updating', key, 'in project from', project[key], 'to', project_values[key]);
				project[key] = project_values[key];
			}
			/*let response =*/await axios.put(`${API_BASE_URL}api/projet/${project_values.id}`, project);
			// console.log("project successfully saved", response);
		} catch (error) {
			console.error("Vuex action update_study_and_project - error while saving updated project data", error);
		}

		const study_values = data.study_values;

		let titre = project_values.adresse.adresse.split(/\d{5}/)[0].trim();
		if (_.isUndefined(titre) || titre.length === 0) {
			titre = project_values.adresse.adresse;
		}
		study_values.etudeData.titre = titre;
		study_values.etudeData.sous_titre = `${project_values.adresse.ville} (${project_values.adresse.codeInsee})`;

		try {
			const keys_to_keep = ["nom", "evalisReference", "zohoReference", "etudeType","segment", "statut"];
			for (const key of _.intersection(_.keys(study_values), keys_to_keep)) {
				// console.log("updating", key, "in active_study from", state.active_study[key], "to", study_values[key]);
				commit("EDIT_OBJECT", {
					obj: state.active_study,
					field: key,
					value: study_values[key],
				});
			}
			const etude_data_keys_to_keep = ["titre","sous_titre"];
			for (const key of _.intersection(_.keys(study_values.etudeData), etude_data_keys_to_keep)) {
				// console.log("updating", key, "in active_study.etudeData from", state.active_study.etudeData[key], "to", study_values[key]);
				commit("EDIT_OBJECT", {
					obj: state.active_study.etudeData,
					field: key,
					value: study_values.etudeData[key],
				});
			}
			if (refresh_stat_data){
				await dispatch("refresh_stat_data");
			}
			setTimeout(() => {
				dispatch("save_study");
				dispatch("save_required");
			}, 3000);
		} catch (error) {
			console.error("Vuex action update_study_and_project - error while saving updated study data", error);
		}

		dispatch("set_saving", { saving: false });
	},

	publish_active_study ({ commit, dispatch }) {
		//1 - set publication in progress
		commit("SET_STUDY_STATE", { publication_in_progress: true });

		//2 - send publish action to back (publish to evalis)
		dispatch("publish_to_evalis", {
			callback: function (cancelled) {
				commit("SET_STUDY_STATE", { publication_in_progress: false });
				dispatch("add_notification", {
					status: "success",
					message: cancelled ? "Publication annulée. " : "Publication effectuée.",
				});
				if (!cancelled){
					commit("PUBLISH_STUDY");
				}
			},
			error: function () {
				commit("SET_STUDY_STATE", { publication_in_progress: false });
				commit("SET_STUDY_STATE", { publication_failed: true });
			},
		});
	},
	publish_to_evalis ({ commit, dispatch, state},data){
		try {
			const body = data.overrideEvalisReference ? {"overrideEvalisReference": data.overrideEvalisReference} : null;
			axios.post(`${API_BASE_URL}api/etude/publish/${state.active_study.id}`, body)
				.then(function (response) {
					if (response.status == 200){
						data.callback(false);
					}
					else if (response.status == 202){
						let confirmationMessage = "Des études publiées dans d'autres projets possèdent déjà cette référence EVALIS.\nVoulez-vous passer ces études dans le statut 'en cours' afin de publier cette étude ? (les documents de ces études seront conservés, mais ne seront plus accessible depuis le portail)\n";
						for (const etude of response.data.content){
							confirmationMessage += "\n\u2022 " + etude;
						}
						if (confirm(confirmationMessage)){
							data["overrideEvalisReference"] = true;
							dispatch("publish_to_evalis",data);
						} else {
							data.callback(true);
						}
					}
					else {
						commit("SET_STUDY_STATE", { publication_message: state.publication_message_default });
						data.error();
					}
				})
				.catch(function (err) {
					data.error();
					if (err.response.status / 100 == 4)
						commit("SET_STUDY_STATE", { publication_message: err.response.data.message });
				})			;

		} catch (error) {
			commit("SET_STUDY_STATE", { publication_message: state.publication_message_default });
			data.error();
		}
	},
	async check_evalis_code ({ commit, dispatch, state},data){
		let evalisReferenceValidated = "";
		let result = false;
		try {
			if (data == ""){
				try {
					commit("EDIT_OBJECT", {
						obj: state.active_study,
						field: "evalisReference",
						value: "",
					});
				} catch (error){
					//if an error occured is due to creation mode - OK
				}
			}
			// console.log("check_evalis_code / data : ",data)
			await axios.get(`${API_BASE_URL}api/evalis/api/check-code-evalis/${data}`)
				.then(function (response) {
					if (response.status == 200){
						evalisReferenceValidated = data;
						result = true;
					} else {
						evalisReferenceValidated = "";
					}
				})
				.catch(function () {
					evalisReferenceValidated = "";
				})
				.finally( function (){
					try {
						commit("EDIT_OBJECT", {
							obj: state.active_study,
							field: "evalisReference",
							value: evalisReferenceValidated
						});
					} catch (error){
						//if an error occured is due to creation mode - OK
					}
					dispatch("save_study");
					dispatch("save_required");
				});
		} catch (error) {
			try {
				commit("EDIT_OBJECT", {
					obj: state.active_study,
					field: "evalisReference",
					value: "",
				});
			} catch (error){
				//if an error occured is due to creation mode - OK
			}
		} finally {
			return result;
		}
	},
	code_evalis_failed ({ commit }) {
		commit("SET_STUDY_STATE", { publication_in_progress: false });
		commit("SET_STUDY_STATE", { publication_message: "Veuillez vérifier le Code Evalis!" });
		commit("SET_STUDY_STATE", { publication_failed: true });
	},

	unpublish_active_study ({ commit, dispatch, state }) {
		// remove pdf
		dispatch("edit_object", {
			obj: state.active_study,
			field: "pdfLien",
			value: "",
		});

		// 1 - set status to "publié"
		commit("UNPUBLISH_STUDY");
		dispatch("save_study", {
			callback: function () {
				dispatch("add_notification", {
					status: "success",
					message: "A vous de jouer !",
				});
				dispatch("set_saving", { saving: false });
			},
		});
	},

	edit_study: function ({ commit, dispatch }, data) {
		if (data.study) {
			commit("EDIT_STUDY", data);
			commit("SET_STUDY_STATE", {
				changed: true,
			});
			dispatch("save_study");
		}
	},

	edit_object: function ({ commit, dispatch }, data) {
		commit("EDIT_OBJECT", {
			obj: data.obj,
			field: data.field,
			value: data.value,
		});
		commit("SET_STUDY_STATE", { changed: true });
		dispatch("save_study");

	},
	launch_saving_interval: function ({dispatch, commit}){
		dispatch("save_study");
		dispatch("save_required");
		if (!state.saveInterval){
			const interval = window.setInterval(function (){dispatch("save_required");}, 5000);
			commit("SET_STUDY_STATE", { saveInterval: interval });
		}
	},
	stop_saving_interval ({commit}){
		commit("SET_STUDY_STATE", { saving: false});
		commit("SET_STUDY_STATE", { required: 0});
		commit("SET_STUDY_STATE", { last_data: false});
		// console.log("stop saving interval")
		window.onbeforeunload = null;
		if (state.saveInterval){
			clearInterval(state.saveInterval);
			commit("SET_STUDY_STATE", { saveInterval: false });
		}
	},
	save_required ({ commit, dispatch, state}){
		const data = state.last_data;
		if (state.required > 0 && !state.saving && state.active_study){
			commit("SET_STUDY_STATE", { required: 1});
			dispatch("set_saving", { saving: true });
			commit("SET_STUDY_STATE", { last_save_failed: false });
			const config = {
				method: "put",
				url: `${API_BASE_URL}api/etude/${state.active_study.id}`,
				data: state.active_study,
			};
			
			axios(config).then(
				function () {
					commit("EDIT_OBJECT", {
						obj: state.active_study.etudeData,
						field: "last_save_date",
						value: moment().format(),
					});
					commit("EDIT_OBJECT", {
						obj: state.active_study.etudeData,
						field: "last_save_user_id",
						value: user.state.user_details.id,
					});
					commit("SET_STUDY_STATE", { changed: false });
					// No need to save & warn before browser closure
					window.onbeforeunload = null;
					if (data && data.callback) {
						// console.log("callback after saving study");
						data.callback();
					}
					// console.log("save required : "+state.required);
					commit("SET_STUDY_STATE", { required: state.required <= 0 ? 0 : state.required - 1});
					if (state.required > 1){
						commit("SET_STUDY_STATE", { required: 1 });
					}
					// console.log("save done :"+state.required);
					dispatch("set_saving", { saving: false });
				}).catch(function (error) {
				console.error("ERROR while saving the study", error);
				commit("SET_STUDY_STATE", { last_save_failed: true });
				commit("SET_STUDY_STATE", { required: 2});
				commit("SET_STUDY_STATE", { saving: false});
			});
		}
	},
	save_study ({commit}, data) {
		commit("SET_STUDY_STATE", { changed: true });
		commit("SET_STUDY_STATE", { last_data: data});
		commit("SET_STUDY_STATE", { required: state.required + 1 });
		// console.log(state.required);
		// console.log("save_study");
		// Need to save and warn before closure
		window.onbeforeunload = function (){
			return "Patientez, enregistrement en cours...";
		};
	},

	async delete_study ({ commit, dispatch }, data) {
		dispatch("set_saving", { saving: true });
		try {
			await axios.delete(`${API_BASE_URL}api/etude/${data.study_id}`);
			commit("SET_STUDY_STATE", { changed: false });
		} catch (error) {
			console.error("ERROR while deleting the study", error);
		}
		if (data && data.callback) {
			// console.log("callback after saving study");
			data.callback();
		}
		dispatch("set_saving", { saving: false });
	},

	set_saving: function ({ commit }, data) {
		commit("SET_STUDY_STATE", {
			saving: data.saving,
		});
	},
	set_required: function ({ commit }, data) {
		commit("SET_STUDY_STATE", {
			required: data.required,
		});
	},
	set_offline: function ({ commit }, data) {
		commit("SET_STUDY_STATE", {
			offline: data.offline,
		});
	},
	set_last_save_failed: function ({ commit }, data) {
		commit("SET_STUDY_STATE", {
			last_save_failed: data.last_save_failed,
		});
	},
	set_active_study: function ({ commit }, data) {
		commit("SET_STUDY_STATE", { active_study: data.active_study });
	},

	async refresh_kyaneos_data ({ commit, state }, { id }) {
		try {
			if (state.active_study.segment.code === "SCPI") {
				const kyaneosData = await study_utils.get_kyaneos_scpi_data({ id });

				commit("UPDATE_KYANEOS_DATA", { kyaneosData });
				// update the date of the statistical_data
				commit("EDIT_OBJECT", {
					obj: state.active_study.etudeData,
					field: "kyaneos_data_date",
					value: moment().format(),
				});
				commit("EDIT_OBJECT", {
					obj: state.active_study.etudeData,
					field: "refreshKyaneosData",
					value: false,
				});
				commit("SET_STUDY_STATE", { changed: true });
			}
		} catch (error) {
			console.error("ERROR in refresh_kyaneos_data while retrieving data for Project Guid == ", id, error);
		}
	},

	async refresh_yanport_data ({ commit, state }, { codeInsee = "" }, page = null) {
		try {
			if (state.active_study.segment.code === "SCPI") {
				if (page == "page_4" || page == null){
					const yanportData4 = await study_utils.get_yanport_scpi_data({
						codeInsee,
						forceVille: state.active_study.etudeData.scpi.yanport["page_4"].localisations,
						searchedData: ["tableauLocatif", "kpiLocatif"],
						typeBiens: state.active_study.etudeData.scpi.yanport["page_4"].type_biens,
						periodeAnalyse: state.active_study.etudeData.scpi.yanport["page_4"].periode_analyse
					});
	
					commit("UPDATE_YANPORT_DATA", { yanportData4, save_parent: "yanportData4" });
				}
				if (page == "page_5" || page == null){
					const yanportData5 = await study_utils.get_yanport_scpi_data({
						codeInsee,
						forceVille: state.active_study.etudeData.scpi.yanport["page_5"].localisations,
						searchedData: ["kpiAncien"],
						typeBiens: state.active_study.etudeData.scpi.yanport["page_5"].type_biens,
						periodeAnalyse: state.active_study.etudeData.scpi.yanport["page_5"].periode_analyse
					});
	
					commit("UPDATE_YANPORT_DATA", { yanportData5, save_parent: "yanportData5" });
				}

				// update the date of the statistical_data
				commit("EDIT_OBJECT", {
					obj: state.active_study.etudeData,
					field: "yanport_data_date",
					value: moment().format(),
				});
				commit("EDIT_OBJECT", {
					obj: state.active_study.etudeData,
					field: "refreshYanportData",
					value: false,
				});
				commit("SET_STUDY_STATE", { changed: true });
			}
		} catch (error) {
			console.error("ERROR in refresh_yanport_data while retrieving data for Project Guid == ", codeInsee, error);
		}
	},

	async refresh_stat_data ({ commit, state }) {
		const insee_code = state.active_study.projet.adresse.codeInsee;

		try {
			const qlikData = await study_utils.new_get_qlik_data({
				codeInsee: insee_code,
				segmentId: state.active_study.segment.id,
				etudeTypeId: state.active_study.etudeType.id,
				projectLat: state.active_study.projet.position.location.lat,
				projectLon: state.active_study.projet.position.location.lon
			});

			commit("UPDATE_STAT_DATA", {qlikData});
			// update the date of the statistical_data
			commit("EDIT_OBJECT", {
				obj: state.active_study.etudeData,
				field: "statistical_data_date",
				value: moment().format(),
			});
			commit("EDIT_OBJECT", {
				obj: state.active_study.etudeData,
				field: "refreshQlikData",
				value: false,
			});
			commit("SET_STUDY_STATE", { changed: true });
		} catch (error) {
			console.error("ERROR in refresh_stat_data while retrieving stats data for CodeInsee == ", insee_code, error);
		}
	},

	async upload_file_to_project ({ commit, dispatch, state }, data) {
		if (!data || !data.the_file) {
			return console.error("VueX upload_file_to_project - data should contain the_file property");
		}
		// console.log("uploading file", data.the_file, "and save it to project.pieceJointe");
		const name = data.the_file.name;
		const lastDot = name.lastIndexOf(".");
		const filename = name.substring(0, lastDot);
		const ext = name.substring(lastDot + 1);
		// console.log(name, filename, ext);
		try {
			const project_id = state.active_study.projet.id;
			const response = await upload_file(data, project_id);
			// response.data.content should contain the URL of the uploaded image
			// console.log("upload file response", response);
			// compose file object
			const file_object = {
				id: moment().format("x"), // UNIX timestamp with milliseconds
				name: filename,
				type: ext,
				icon: study_utils.ext_to_icon(ext),
				date: moment().format(),
				url: response.data.content.url,
			};

			// console.log("will add", file_object, "to project pieceJointe");

			const project = state.active_study.projet;
			let pj = [];
			if (project.pieceJointe && project.pieceJointe.length) {
				pj = project.pieceJointe;
			}
			pj.push(file_object);

			commit("SET_PROJECT_PIECEJOINTE", { project: project, files: pj });
			dispatch("save_project_pieceJointe", { project: project });
		} catch (error) {
			console.error("async upload file - error with API /Projet/$id/Upload - $id =", project_id);
		}
	},

	async save_project_pieceJointe ({ dispatch }, data) {
		if (!data || !data.project) {
			return console.error("VueX action - save_project - invalid data:", data);
		}

		dispatch("set_saving", { saving: true });
		const project_id = data.project.id;
		try {
			// 1 - reload the full project
			const r = await axios.get(API_BASE_URL + "api/projet/" + project_id);
			const project = r.data.content;
			// console.log("loaded project", project);

			// 2 - set project.pieceJointe
			project.pieceJointe = data.project.pieceJointe;

			// 3 - save project
			try {
				await axios.put(`${API_BASE_URL}api/projet/${project_id}`, project);
				// console.log("project successfully saved", r);

				if (data && data.callback) {
					// console.log("callback after saving project");
					data.callback();
				}
			} catch (error) {
				console.error("VueX action - save_project - error while saving the project", error);
			}
		} catch (error) {
			console.error("VueX action - save_project - error while loading the project to save", error);
		}

		dispatch("set_saving", { saving: false });
	},

	save_project ({ commit }) {
		try {
			commit("SET_PROJECT_STATE", { changed: false });
		} catch (error) {
			console.error("ERROR while saving the project", error);
		}
	},

	async upload_file ({ dispatch, state }, data) {
		// data should contain the file to save
		if (!data || !data.the_file) {
			return console.error("VueX upload_file - data should contain the_file property", data);
		}
		const project_id = state.active_study.projet.id;
		// console.log("uploading file", data.the_file, "and save it to", data.parent_data, data.property, project_id);
		try {
			const response = await upload_file(data, project_id);
			// response.data.content should contain the URL of the uploaded image
			// console.log("upload file response", response);
			dispatch("edit_object", {
				obj: data.parent_data,
				field: data.property,
				value: response.data.content.url,
			});
		} catch (error) {
			console.error("async upload file - error with API /Projet/$id/Upload - $id =", project_id, error);
		}
	},

	get_donnees_reference_programmes: function ({state, commit}, data) {
		axios.post(`${process.env.VUE_APP_API_BASE_URL}api/efocus/api/GetDataReference`, data,{cancelToken: state.source.token})
			.then(function (r) {
				commit("SET_CARTOGRAPHIE", {"index": "references_prix", "value": r.data.content});
			})
			.catch(function (e) {
				if (e !== "cancelled"){
					console.error(e);
				}
			});
	},

	unlock_modal ({commit}){
		commit("SET_STUDY_STATE", { publication_failed: false });
		commit("SET_STUDY_STATE", { publication_in_progress: false });
	}
};

async function upload_file (data, project_id) {
	const formData = new FormData();
	formData.append("file", data.the_file);
	const headers = { headers: { "Content-Type": "multipart/form-data" } };
	// console.log("formData", formData, headers, project_id);
	const size = data.the_file.size;
	if (size < 1000) {
		alert("Fichier invalide (taille inférieure à 1ko), merci de vérifier votre document");
		throw new Error("Invalid file");
	}
	let url;
	
	if (data.the_file.type.toUpperCase().match(/IMAGE/) == null) {
		url = `${API_BASE_URL}api/projet/${project_id}/upload-file`;
	} else {
		url = `${API_BASE_URL}api/projet/${project_id}/upload-image`;
	}
	try {
		//const response = await axios.post(`${API_BASE_URL}Cdc/Projet/${project_id}/Upload`, formData, headers);
		const response = await axios.post(url, formData, headers);
		return response;
	} catch (error) {
		console.error(error);
	}
}

// === MUTATIONS =====================================================================================
const mutations = {
	SET_STUDY_STATE: function (state, data) {
		for (const i in data) {
			Vue.set(state, i, data[i]);
		}
	},

	SET_LISTS: function (state, data) {
		// console.log(data);
		for (const i in data.lists) {
			Vue.set(state.lists, i, data.lists[i]);
		}
	},

	EDIT_STUDY: function (state, data) {
		if (data.study) {
			for (const i in data) {
				if (i != "study") {
					const obj = data["study"];
					const field = i;
					const value = data[i];
					Vue.set(obj, field, value);
				}
			}
		}
	},

	EDIT_OBJECT: function (state, data) {
		// console.log('MUTATION EDIT_OBJECT', data);
		// Vue.set(data.obj, data.field, data.value);
		deepSet(data.obj, data.field, data.value);
	},

	EDIT_OPERATIONS_REFERENCE: function (state, data) {
		Vue.set(state, "operations_reference", data.operations_reference);
	},

	UPDATE_STAT_DATA (state, data) {
		state.active_study.qlikData = data.qlikData;

		try {
			const c = state.active_study.qlikData.component.find((c) => {return c.id == "1.1";});
			if (c) {
				if (!c.content.commune[0].data_list[0].value.match(/^[0-9]*$/) && c.content.commune[0].data_list[0].value.match(/[0-9]+/).length) {
					c.content.commune[0].data_list[0].value = c.content.commune[0].data_list[0].value.match(/[0-9]+/)[0];
				}
			}
		} catch (error) {
			console.error("ERROR in UPDATE_STAT_DATA/ get Priority - no priority ", error);
		}

	},

	UPDATE_KYANEOS_DATA (state, data) {
		state.active_study.etudeData.kyaneosData = data.kyaneosData;

		try {
			// const c = state.active_study.qlikData.component.find((c) => {return c.id == "1.1";});
			// if(c) {
			// 	if(!c.content.commune[0].data_list[0].value.match(/^[0-9]*$/) && c.content.commune[0].data_list[0].value.match(/[0-9]+/).length) {
			// 		c.content.commune[0].data_list[0].value = c.content.commune[0].data_list[0].value.match(/[0-9]+/)[0];
			// 	}
			// }
		} catch (error) {
			console.error("ERROR in UPDATE_KYANEOS_DATA/ get Priority - no priority ", error);
		}
	},
	
	UPDATE_YANPORT_DATA (state, data) {
		const parent = data["save_parent"];
		state.active_study.etudeData[parent] = data[parent];
		try {
			// const c = state.active_study.qlikData.component.find((c) => {return c.id == "1.1";});
			// if(c) {
			// 	if(!c.content.commune[0].data_list[0].value.match(/^[0-9]*$/) && c.content.commune[0].data_list[0].value.match(/[0-9]+/).length) {
			// 		c.content.commune[0].data_list[0].value = c.content.commune[0].data_list[0].value.match(/[0-9]+/)[0];
			// 	}
			// }
		} catch (error) {
			console.error("ERROR in UPDATE_YANPORT_DATA/ get Priority - no priority ", error);
		}
	},

	SET_PROJECT_PIECEJOINTE (state, data) {
		if (!data || !data.files || !data.project) {
			console.error("SET_PROJECT_PIECEJOINTE failed, invalid data:", data);
		}
		// console.log("SET_PROJECT_PIECEJOINTE - setting project.pieceJointe to", data.files);
		Vue.set(data.project, "pieceJointe", data.files);
	},

	PUBLISH_STUDY (state) {
		Vue.set(state.active_study, "statut", _.find(state.lists.statuts, ["slug", "publie"]));
	},

	UNPUBLISH_STUDY (state) {
		Vue.set(state.active_study, "statut", _.find(state.lists.statuts, ["slug", "en-cours"]));
	},

	SET_CARTOGRAPHIE (state, data) {
		if (state.active_study) {
			if (!state.active_study.etudeData.cartographie) state.active_study.etudeData.cartographie = {};
			state.active_study.etudeData.cartographie[data.index] = data.value;
		}
	},

	SET_PROJET_URBAIN (state, data) {
		if (state.active_study) {
			if (!state.active_study.etudeData.projet_urbain) state.active_study.etudeData.projet_urbain = {};
			state.active_study.etudeData.projet_urbain[data.index] = data.value;
		}
	},

	SET_COMPONENT (state, data) {
		if (state.active_study && state.active_study.qlikData) {
			state.active_study.qlikData.component[data.id] = data.content;
		}
	}
};

export default { state, getters, actions, mutations };
